import { FormEvent, useState } from 'react';
import {
  Mail,
  Lock,
  CheckCircle2,
  Loader2,
  Fingerprint,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { loginWithPasskey, webauthnSupported } from '../api/webauthn';

// Connexion à l'espace élève (refonte du 14/10/2026) : mêmes modes (mot de passe, lien par e-mail, FaceID),
// habillage de la charte.

type Mode = 'password' | 'magic';

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useAuthStore((s) => s.login);
  const loggingIn = useAuthStore((s) => s.loggingIn);
  const requestLink = useAuthStore((s) => s.requestLink);
  const resetFlow = useAuthStore((s) => s.resetFlow);
  const linkRequested = useAuthStore((s) => s.linkRequested);
  const requesting = useAuthStore((s) => s.requesting);
  const authError = useAuthStore((s) => s.authError);

  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);

  const onBiometric = async () => {
    setBioBusy(true);
    setBioError(null);
    try {
      await loginWithPasskey();
    } catch {
      setBioError(
        'Connexion biométrique impossible. Utilisez votre mot de passe, ou activez FaceID depuis votre espace après connexion.',
      );
    } finally {
      setBioBusy(false);
    }
  };

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    void login(email.trim(), password);
  };

  const onSubmitMagic = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    void requestLink(email.trim());
  };

  const switchMode = (next: Mode) => {
    resetFlow();
    setBioError(null);
    setMode(next);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-hero-indigo px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo-permio-eleve.png" alt="Permio" className="h-9 w-auto" />
        </div>

        <div className="rounded-card bg-white p-6 shadow-card sm:p-8">
          {/* ── Confirmation d'envoi du lien magique ── */}
          {mode === 'magic' && linkRequested ? (
            <div className="text-center space-y-4">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success-light">
                <CheckCircle2 className="text-success" size={28} />
              </div>
              <div>
                <h1 className="text-title-md text-heading">Vérifiez votre boîte mail</h1>
                <p className="mt-2 text-sm leading-relaxed text-secondary">
                  Si un compte candidat est associé à cette adresse, un lien de
                  connexion vient d’être envoyé. Il est valable 15 minutes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => switchMode('password')}
                className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 hover:underline"
              >
                Revenir à la connexion par mot de passe
              </button>
            </div>
          ) : mode === 'password' ? (
            /* ── Connexion email + mot de passe (par défaut) ── */
            <>
              <div className="text-center mb-6">
                <h1 className="text-title-md text-heading">Mon espace candidat</h1>
                <p className="mt-2 text-sm leading-relaxed text-secondary">
                  Connectez-vous avec votre email et votre mot de passe.
                </p>
              </div>

              <form onSubmit={onSubmitPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-[13px] font-medium text-body"
                  >
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-[13px] font-medium text-body"
                  >
                    Mot de passe
                  </label>
                  <div className="relative">
                    <Lock
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    />
                  </div>
                </div>

                {authError && (
                  <p className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark" role="alert">
                    {authError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loggingIn}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-pill bg-btn-primary px-4 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-60"
                >
                  {loggingIn && <Loader2 size={16} className="animate-spin" />}
                  Se connecter
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => switchMode('magic')}
                  className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 hover:underline"
                >
                  Mot de passe oublié ou première connexion ? Recevoir un lien
                  par email
                </button>
              </div>

              {webauthnSupported() && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="h-px flex-1 bg-line" />
                    <span className="text-xs text-muted">ou</span>
                    <div className="h-px flex-1 bg-line" />
                  </div>
                  <button
                    type="button"
                    onClick={onBiometric}
                    disabled={bioBusy}
                    className="flex h-11 w-full items-center justify-center gap-2 rounded-pill border border-line bg-white px-4 text-sm font-medium text-body transition-all enabled:hover:-translate-y-px enabled:hover:border-primary-200 enabled:hover:shadow-navbar disabled:opacity-60"
                  >
                    {bioBusy ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Fingerprint size={16} className="text-primary" />
                    )}
                    Se connecter avec FaceID / Touch ID
                  </button>
                  {bioError && (
                    <p className="mt-2 rounded-xl bg-danger-light px-3 py-2 text-xs text-danger-dark" role="alert">
                      {bioError}
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            /* ── Connexion par lien magique (secondaire) ── */
            <>
              <div className="text-center mb-6">
                <h1 className="text-title-md text-heading">Connexion par email</h1>
                <p className="mt-2 text-sm leading-relaxed text-secondary">
                  Saisissez votre email : nous vous enverrons un lien de
                  connexion sécurisé, sans mot de passe. Vous pourrez ensuite
                  définir un mot de passe depuis votre espace.
                </p>
              </div>

              <form onSubmit={onSubmitMagic} className="space-y-4">
                <div>
                  <label
                    htmlFor="magic-email"
                    className="mb-1.5 block text-[13px] font-medium text-body"
                  >
                    Adresse email
                  </label>
                  <div className="relative">
                    <Mail
                      size={18}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <input
                      id="magic-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.fr"
                      className="h-11 w-full rounded-xl border border-line bg-surface-light pl-10 pr-3 text-[15px] text-body outline-none transition-all placeholder:text-muted focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
                    />
                  </div>
                </div>

                {authError && (
                  <p className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark" role="alert">
                    {authError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={requesting}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-pill bg-btn-primary px-4 text-sm font-semibold text-white shadow-btn transition-all enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover disabled:opacity-60"
                >
                  {requesting && <Loader2 size={16} className="animate-spin" />}
                  Recevoir mon lien de connexion
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => switchMode('password')}
                  className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 hover:underline"
                >
                  Revenir à la connexion par mot de passe
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-secondary">
          Un souci de connexion ? Contactez votre auto-école.
        </p>
      </div>
    </div>
  );
}
